/**
 * Issue #215 — 通话质检报告详情（call-detail）展示决策的纯函数。
 *
 * 这里只承载「通话转写 / AI纠错」版块的可见性判定：完整通话文本属敏感数据，后端 (/calls/:id)
 * 已经只对 ADMIN / SUPER_ADMIN 下发 qcSummary.rawTranscript / correctedTranscript（其余角色为 null），前端
 * 再据角色决定渲染原文、空态还是「无权查看」。把判定抽成纯函数便于单测，组件只负责
 * 渲染返回的视图状态。
 *
 * 「问题点 / 客户意向 / 建议话术」三段直接消费后端结构化字段
 * （qcSummary.problemPoints / customerIntent / suggestedScripts），与移动端读取的
 * 是同一份 AI 质检结果，无需前端再加工，故不在此重排或改写。
 */
import type { Role } from './types';
import { isAdminRole } from './auth-store';

/**
 * Issue #227 — 当 AI 质检完成但「问题点 / 建议话术」确无内容时展示的统一备注。
 * 取代旧的「AI 确认未发现问题点」「AI 未给出额外建议话术」两段空态文案。
 */
export const QC_NO_FINDINGS_NOTE = 'AI诊断无需建议/问题点';

export type TranscriptView =
  /** 当前角色无权查看 ASR 原文（MANAGER 及以下）。 */
  | { kind: 'forbidden' }
  /** 有权查看，但本通话尚未生成 ASR 文本。 */
  | { kind: 'empty' }
  /** 有权查看且已生成：渲染原文。 */
  | { kind: 'text'; text: string };

/**
 * 决定「通话转写」版块如何渲染。
 *
 * - 非 ADMIN / SUPER_ADMIN：一律 forbidden（即便后端漏下发，也不展示）。
 * - ADMIN / SUPER_ADMIN 且 transcript 为空 / 仅空白：empty（尚未生成）。
 * - ADMIN / SUPER_ADMIN 且有内容：text（去除首尾空白后的原文）。
 */
export function resolveTranscriptView(
  role: Role | undefined | null,
  transcript: string | null | undefined,
): TranscriptView {
  if (!isAdminRole(role)) {
    return { kind: 'forbidden' };
  }
  const text = typeof transcript === 'string' ? transcript.trim() : '';
  if (text.length === 0) {
    return { kind: 'empty' };
  }
  return { kind: 'text', text };
}

/**
 * Issue #397 — 「AI纠错/重排版」展示历史未回填记录（无 formattedTranscript）时的统一提示文案。
 *
 * formattedTranscript 现直接镜像 correctedTranscript（管理端「AI文本纠错」单次输出即最终文本），
 * 因此回退展示的 correctedTranscript 与最终文本内容一致——文案不得暗示「缺少独立重排过程」。
 * 单一事实源，避免前端多处硬编码分叉；测试据此固化文案。
 */
export const QC_REFORMAT_FALLBACK_NOTE = '当前展示 AI文本纠错结果（该历史记录字段未回填，内容与最终文本一致）';

/**
 * Issue #397 — 「AI纠错/重排版」版块渲染决策。
 *
 * 展示后端下发的 AI文本纠错最终文本（formattedTranscript，直接镜像 correctedTranscript）；
 * 历史未回填的记录缺 formattedTranscript 时，回退展示内容一致的 correctedTranscript 并标注
 * （{@link QC_REFORMAT_FALLBACK_NOTE}）。两者皆无则空态。RBAC 与「通话转写」一致：非 ADMIN /
 * SUPER_ADMIN 一律 forbidden（即便后端误下发也不渲染）。**不再有第二次独立 AI 重排过程**。
 */
export type ReformatView =
  /** 当前角色无权查看完整通话文本（MANAGER 及以下）。 */
  | { kind: 'forbidden' }
  /** 有权查看，但既无最终文本也无纠错文本。 */
  | { kind: 'empty' }
  /** 展示 AI文本纠错最终文本（后端已下发 formattedTranscript）。 */
  | { kind: 'formatted'; text: string }
  /** 历史未回填：回退展示内容一致的 correctedTranscript（需在 UI 标注）。 */
  | { kind: 'fallback'; text: string };

export function resolveReformatView(
  role: Role | undefined | null,
  formattedTranscript: string | null | undefined,
  correctedTranscript: string | null | undefined,
): ReformatView {
  if (!isAdminRole(role)) {
    return { kind: 'forbidden' };
  }
  const formatted =
    typeof formattedTranscript === 'string' ? formattedTranscript.trim() : '';
  if (formatted.length > 0) {
    return { kind: 'formatted', text: formatted };
  }
  const corrected =
    typeof correctedTranscript === 'string' ? correctedTranscript.trim() : '';
  if (corrected.length > 0) {
    return { kind: 'fallback', text: corrected };
  }
  return { kind: 'empty' };
}

/**
 * Issue #391：仅做前端展示排版，不改写内容语义。把常见说话人前缀拆到新行，
 * 归一化换行，方便管理员阅读 AI 纠错后长文本。
 */
export function formatTranscriptForDisplay(text: string): string {
  const normalized = text
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (normalized.length === 0) return normalized;
  return normalized
    .replace(
      /([^\n])\s*(?=((?:客户|销售|员工|坐席|顾问|客服)[：:]|\[(?:AGENT|CUSTOMER|UNKNOWN)\]\s*))/g,
      '$1\n',
    )
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Issue #217 — 质检分析版块（问题点 / 客户意向 / 建议话术 / 维度得分）的展示状态。
 *
 * 验收要求「区分 AI 确认无问题 与 字段未生成 / 解析失败」：单看 `hasContent` 无法分辨，
 * 必须结合后端处理状态机 `processingStatus`：
 *   - PENDING                : 尚未触发评分（'pending'）。
 *   - PROCESSING             : 重跑进行中，旧结果已被清空（'processing'）。
 *   - FAILED                 : AI 评分 / 字段生成失败（'failed' + 可读 reason）。
 *   - COMPLETED 且本段无内容  : AI 跑完但该段为空 → 「AI 确认无问题 / 无建议」（'empty'）。
 *   - COMPLETED 且有内容      : 正常渲染（'ready'）。
 */
export type QcAnalysisState =
  | { kind: 'pending' }
  | { kind: 'processing' }
  | { kind: 'failed'; reason: string | null }
  | { kind: 'empty' }
  | { kind: 'ready' };

/** 终态：评分流水线不会再推进（COMPLETED / FAILED）。其余为进行中（含未知值兜底进行中）。 */
function normalizeProcessingStatus(
  status: string | null | undefined,
): 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' {
  const s = (status ?? '').toString().trim().toUpperCase();
  if (s === 'COMPLETED') return 'COMPLETED';
  if (s === 'FAILED' || s === 'DLQ' || s === 'ERROR' || s === 'TIMEOUT') {
    return 'FAILED';
  }
  if (s === 'PROCESSING') return 'PROCESSING';
  // PENDING 或任何未知 / 空值：当作尚未完成处理。
  return 'PENDING';
}

export function resolveQcAnalysisState(
  processingStatus: string | null | undefined,
  failureReason: string | null | undefined,
  hasContent: boolean,
): QcAnalysisState {
  const status = normalizeProcessingStatus(processingStatus);
  if (status === 'FAILED') {
    const reason =
      typeof failureReason === 'string' && failureReason.trim().length > 0
        ? failureReason.trim()
        : null;
    return { kind: 'failed', reason };
  }
  if (status === 'PROCESSING') return { kind: 'processing' };
  if (status === 'PENDING') return { kind: 'pending' };
  // COMPLETED：有内容才 ready，否则是「跑完但为空」。
  return hasContent ? { kind: 'ready' } : { kind: 'empty' };
}

/**
 * 是否应继续轮询通话详情（重跑后异步评分进行中）。PENDING / PROCESSING 时为 true；
 * 终态（COMPLETED / FAILED / DLQ…）为 false。前端据此设置 react-query refetchInterval，
 * 实现「页面不刷新也能看到任务状态与最终结果」。
 */
export function shouldPollQc(processingStatus: string | null | undefined): boolean {
  const status = normalizeProcessingStatus(processingStatus);
  return status === 'PENDING' || status === 'PROCESSING';
}
